import { SCHEMA_VERSION } from '../model/constants';
import { envelopeSchema } from '../model/envelope-schema';
import type { RecordType } from '../model/enums';
import type { AnyEnvelope, DataOf, Envelope } from '../model/types';
import { compareHlc } from '../hlc/hlc';

export interface MakeEnvelopeInput<T extends RecordType> {
  readonly id: string;
  readonly type: T;
  /** `null` creates a tombstone (deleted record). */
  readonly data: DataOf<T> | null;
  readonly hlc: string;
  readonly deviceId: string;
}

/** Build an envelope; `data === null` yields a deleted record. */
export function makeEnvelope<T extends RecordType>(input: MakeEnvelopeInput<T>): Envelope<T> {
  return {
    id: input.id,
    type: input.type,
    hlc: input.hlc,
    deleted: input.data === null,
    schemaVersion: SCHEMA_VERSION,
    deviceId: input.deviceId,
    data: input.data,
  } as Envelope<T>;
}

/** A copy of `record` rewritten as a tombstone with a new timestamp. */
export function tombstoneOf<E extends AnyEnvelope>(record: E, hlc: string, deviceId: string): E {
  return { ...record, hlc, deviceId, deleted: true, data: null };
}

/** A copy of `record` stamped with a new timestamp and author device. */
export function restamp<E extends AnyEnvelope>(record: E, hlc: string, deviceId: string): E {
  return { ...record, hlc, deviceId };
}

export type EnvelopeParseResult =
  | { readonly success: true; readonly data: AnyEnvelope }
  | { readonly success: false; readonly issues: readonly string[] };

/** Validate an unknown value as a record envelope (system boundary: import, sync, writes). */
export function parseEnvelope(value: unknown): EnvelopeParseResult {
  const parsed = envelopeSchema.safeParse(value);
  if (parsed.success) return { success: true, data: parsed.data };
  return {
    success: false,
    issues: parsed.error.issues.map((issue) => `${issue.path.join('.') || '(root)'}: ${issue.message}`),
  };
}

/**
 * Last-writer-wins: the incoming record replaces the existing one only when its timestamp is
 * strictly newer. A missing existing record always loses.
 */
export function lwwWins(
  incoming: { readonly hlc: string },
  existing: { readonly hlc: string } | null | undefined,
): boolean {
  return existing === null || existing === undefined || compareHlc(incoming.hlc, existing.hlc) > 0;
}
